import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Anchor, Compass, Wrench, Fuel, Receipt, Sparkles, Cloud, ShieldCheck, ChevronDown, Smartphone, Camera, Settings, BarChart3, Globe, Users, Navigation, History, Quote, Lock, CreditCard, MonitorSmartphone, Heart, Play, X, MapPin } from "lucide-react";
import { isNativeApp } from "@/lib/platform";
import { api } from "@/lib/api";
import { InstallButton } from "@/components/InstallPrompt";

// Store URLs — kept module-level so Hero, WhatsNew and GetTheApp all
// use the same source of truth. Both submitted Feb 8, 2026.
const APPLE_STORE_URL = "https://apps.apple.com/app/id6770087383";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=app.bosunsmate";

export default function Landing() {
    const nav = useNavigate();
    const native = isNativeApp();
    return (
        <div data-testid="landing-page" style={{ minHeight: "100vh" }}>
            <TopNav nav={nav} />
            <Hero nav={nav} />
            <ConnectionSection nav={nav} />
            <VideoWalkthrough />
            <WhatsNew />
            <HowItWorks />
            <ScreenshotGallery />
            <FeatureGrid />
            <VesselCollage />
            <Testimonials />
            {!native && <PricingTable nav={nav} />}
            <GetTheApp />
            <Faq />
            <CtaStrip nav={nav} />
            <Footer />
            {!native && <FloatingPlayPill />}
        </div>
    );
}

function TopNav({ nav }) {
    return (
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 18px", maxWidth: 1280, margin: "0 auto", position: "relative", zIndex: 2, gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <img
                    src="/brand/bm-anchor-mark.png"
                    alt=""
                    width="34"
                    height="34"
                    data-testid="nav-logo-mark"
                    style={{
                        borderRadius: 8,
                        boxShadow: "0 4px 16px -4px rgba(212, 181, 114, 0.28), inset 0 0 0 1px rgba(212, 181, 114, 0.35)",
                        flexShrink: 0,
                    }}
                />
                <span className="bm-display" style={{ fontSize: 22, whiteSpace: "nowrap" }}>Bosun&apos;s Mate</span>
            </div>
            <nav style={{ display: "flex", gap: 18, alignItems: "center" }} className="bm-mobile-hide">
                <a href="#features" data-testid="nav-features-link" className="bm-link" style={{ fontSize: 14 }}>Features</a>
                <a href="#pricing" data-testid="nav-pricing-link" className="bm-link" style={{ fontSize: 14 }}>Pricing</a>
                <Link to="/blog" data-testid="nav-blog-link" className="bm-link" style={{ fontSize: 14 }}>Blog</Link>
                <a href="#faq" data-testid="nav-faq-link" className="bm-link" style={{ fontSize: 14 }}>FAQ</a>
            </nav>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button data-testid="nav-login-btn" className="bm-btn bm-btn-ghost bm-mobile-shrink" onClick={() => nav("/login")}>Sign in</button>
                <button data-testid="nav-signup-btn" className="bm-btn bm-btn-gold bm-mobile-hide" onClick={() => nav("/register")}>Start free</button>
            </div>
        </header>
    );
}

function Hero({ nav }) {
    return (
        <section style={{ position: "relative", overflow: "hidden", paddingBottom: 36 }}>
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, backgroundImage: "url(https://customer-assets.emergentagent.com/job_boat-live/artifacts/qrw8grhj_20240808_130906.jpg)", backgroundSize: "cover", backgroundPosition: "center 35%", opacity: 0.22, filter: "saturate(0.9)" }} />
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "radial-gradient(900px 500px at 50% 0%, rgba(5,15,28,0.30) 0%, rgba(5,15,28,0.92) 75%, var(--bm-bg-0) 100%)" }} />
            <div style={{ position: "relative", maxWidth: 1240, margin: "0 auto", padding: "40px 18px 0" }}>
                <div className="bm-hero-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 1fr)", gap: 48, alignItems: "center" }}>
                    {/* Left — copy + CTAs */}
                    <div className="bm-hero-copy">
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "6px 8px 6px 6px", borderRadius: 999, border: "1px solid rgba(212, 181, 114, 0.35)", background: "rgba(212, 181, 114, 0.06)", color: "var(--bm-gold)", fontSize: 12, letterSpacing: "0.24em", textTransform: "uppercase", marginBottom: 24, fontWeight: 500 }}>
                            <img src="/brand/bm-anchor-mark.png" alt="" width="22" height="22" style={{ borderRadius: 4, flexShrink: 0 }} />
                            Free forever · no card required
                        </div>
                        <h1 className="bm-display" style={{ fontSize: "clamp(38px, 5.6vw, 72px)", lineHeight: 1.04, margin: 0, fontWeight: 500 }}>
                            Your boat.<br />Your crew.<br />
                            <em style={{ color: "var(--bm-gold)", fontStyle: "italic" }}>Every harbour you&apos;ve shared.</em>
                        </h1>
                        <p style={{ marginTop: 22, fontSize: 17, color: "var(--bm-text-muted)", maxWidth: 540, lineHeight: 1.6 }}>
                            Engine hours, fuel, maintenance, and the cruising log — plus the address book of every boat you&apos;ve crossed paths with. The logbook that remembers people too.
                        </p>
                        {/* Freemium promise — the second, more visual "Free Forever" beat */}
                        <div data-testid="hero-freemium-pill" style={{ marginTop: 26, display: "inline-flex", flexWrap: "wrap", gap: 10, padding: "12px 16px", borderRadius: 14, background: "linear-gradient(135deg, rgba(212, 181, 114, 0.14), rgba(212, 181, 114, 0.04))", border: "1px solid rgba(212, 181, 114, 0.45)", boxShadow: "0 8px 32px -12px rgba(212, 181, 114, 0.35)", color: "var(--bm-text)", fontSize: 14, alignItems: "center" }}>
                            <span style={{ color: "var(--bm-gold)", fontWeight: 600 }}>Free forever.</span>
                            <span style={{ color: "var(--bm-text-muted)" }}>·</span>
                            <span>1 vessel, unlimited entries</span>
                            <span style={{ color: "var(--bm-text-muted)" }}>·</span>
                            <span>Plus 7 days of Pro on the house</span>
                        </div>
                        <div style={{ marginTop: 24, display: "flex", gap: 12, flexWrap: "wrap" }}>
                            <button data-testid="hero-cta-btn" className="bm-btn bm-btn-gold" style={{ padding: "14px 26px", fontSize: 15 }} onClick={() => nav("/register")}>Start free — no card needed</button>
                            <button
                                data-testid="hero-watch-tour-btn"
                                className="bm-btn bm-btn-ghost bm-watch-tour"
                                style={{ padding: "14px 22px", fontSize: 15 }}
                                onClick={() => {
                                    const el = document.getElementById("walkthrough");
                                    if (el) {
                                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                                        // attempt autoplay after a brief delay to let the scroll begin
                                        setTimeout(() => {
                                            const v = el.querySelector("video");
                                            if (v) v.play().catch(() => {});
                                        }, 420);
                                    }
                                }}
                            >
                                <Play size={14} style={{ fill: "var(--bm-gold)", color: "var(--bm-gold)" }} /> Watch 2-minute tour
                            </button>
                            <button data-testid="hero-secondary-btn" className="bm-btn bm-btn-ghost" style={{ padding: "14px 26px", fontSize: 15 }} onClick={() => nav("/login")}>I already have an account</button>
                        </div>
                        <div data-testid="hero-store-badges" style={{ marginTop: 22, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--bm-gold)" }}>
                                or download
                            </span>
                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                <StoreBadge testid="hero-ios-badge" platform="iOS" title="App Store" subtitle="Download on the" href={APPLE_STORE_URL} compact />
                                <StoreBadge testid="hero-android-badge" platform="Android" title="Google Play" subtitle="Get it on" href={PLAY_STORE_URL} compact />
                            </div>
                        </div>
                        <div style={{ marginTop: 16, fontSize: 12, color: "var(--bm-text-dim)" }}>
                            No credit card to start · Cancel anytime · Lifetime option available
                        </div>
                    </div>

                    {/* Right — phone mockup with real Dashboard screenshot */}
                    <div className="bm-hero-phone-wrap" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <PhoneMockup src="/screenshots/dashboard-phone.png" alt="Bosun's Mate dashboard — engine hours, YTD costs, fuel and maintenance on a real captain's vessel" />
                    </div>
                </div>
            </div>

            {/* Trust strip — always visible right below the hero */}
            <TrustStrip />

            <style>{`
                @media (max-width: 860px) {
                    .bm-hero-grid { grid-template-columns: 1fr !important; gap: 36px !important; text-align: center; }
                    .bm-hero-copy > div:first-child { margin-left: auto; margin-right: auto; }
                    .bm-hero-copy p { margin-left: auto !important; margin-right: auto !important; }
                    .bm-hero-copy > div:nth-last-child(2) { justify-content: center !important; }
                }
            `}</style>
        </section>
    );
}

function PhoneMockup({ src, alt }) {
    return (
        <div
            data-testid="hero-phone-mockup"
            style={{
                position: "relative",
                width: "min(320px, 84vw)",
                aspectRatio: "9 / 19.5",
                borderRadius: 38,
                border: "1px solid rgba(212, 181, 114, 0.32)",
                background: "linear-gradient(180deg, rgba(15,28,46,0.6) 0%, rgba(8,17,32,0.9) 100%)",
                padding: 10,
                boxShadow: "0 30px 90px -25px rgba(0, 0, 0, 0.85), 0 0 0 1px rgba(255,255,255,0.02), 0 0 60px -10px rgba(212, 181, 114, 0.12)",
                overflow: "hidden",
            }}
        >
            {/* Subtle notch */}
            <div aria-hidden="true" style={{
                position: "absolute", top: 18, left: "50%", transform: "translateX(-50%)",
                width: 86, height: 22, borderRadius: 12, background: "#020611", zIndex: 2,
                border: "1px solid rgba(255,255,255,0.04)",
            }} />
            <img
                src={src}
                alt={alt}
                loading="eager"
                style={{
                    display: "block", width: "100%", height: "100%",
                    objectFit: "cover", objectPosition: "top center",
                    borderRadius: 30,
                }}
            />
        </div>
    );
}

function TrustStrip() {
    const items = [
        { icon: CreditCard, label: "No credit card" },
        { icon: Lock, label: "End-to-end encrypted" },
        { icon: MonitorSmartphone, label: "Web · iOS · Android" },
        { icon: Heart, label: "Built by a captain" },
    ];
    return (
        <div
            data-testid="trust-strip"
            style={{
                position: "relative",
                maxWidth: 1100, margin: "44px auto 0", padding: "16px 22px",
                borderTop: "1px solid var(--bm-border-soft)",
                borderBottom: "1px solid var(--bm-border-soft)",
                display: "flex", justifyContent: "center", alignItems: "center",
                gap: 36, flexWrap: "wrap",
            }}
        >
            {items.map((it) => (
                <div key={it.label} style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--bm-text-muted)", fontSize: 12.5, letterSpacing: "0.06em" }}>
                    <it.icon size={14} color="var(--bm-gold)" />
                    <span>{it.label}</span>
                </div>
            ))}
        </div>
    );
}

function FloatingPlayPill() {
    const [hidden, setHidden] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const observerRef = useRef(null);

    useEffect(() => {
        // Respect a session-level dismiss preference
        try {
            if (sessionStorage.getItem("bm_play_pill_dismissed") === "1") {
                setDismissed(true);
            }
        } catch (err) {
            // sessionStorage throws in Safari Private Mode and inside certain
            // sandboxed iframes. Default to "not dismissed" — no need to log.
            if (process.env.NODE_ENV === "development") {
                // eslint-disable-next-line no-console
                console.debug("[FloatingPlayPill] sessionStorage read blocked:", err.message);
            }
        }

        // Wait one tick for the video section to mount
        const t = setTimeout(() => {
            const target = document.getElementById("walkthrough");
            if (!target) return;
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    // Hide the pill once the video section is more than ~25% in view
                    const e = entries[0];
                    setHidden(e.isIntersecting && e.intersectionRatio > 0.25);
                },
                { threshold: [0, 0.25, 0.5] },
            );
            observerRef.current.observe(target);
        }, 200);

        return () => {
            clearTimeout(t);
            if (observerRef.current) observerRef.current.disconnect();
        };
    }, []);

    if (dismissed) return null;

    const scrollAndPlay = () => {
        const el = document.getElementById("walkthrough");
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        setTimeout(() => {
            const v = el.querySelector("video");
            if (v) v.play().catch(() => {});
        }, 420);
    };

    const dismiss = (e) => {
        e.stopPropagation();
        setDismissed(true);
        try {
            sessionStorage.setItem("bm_play_pill_dismissed", "1");
        } catch (err) {
            // Safari Private Mode / sandboxed iframes — pill is already
            // dismissed in component state; persistence is a nice-to-have.
            if (process.env.NODE_ENV === "development") {
                // eslint-disable-next-line no-console
                console.debug("[FloatingPlayPill] sessionStorage write blocked:", err.message);
            }
        }
    };

    return (
        <button
            type="button"
            data-testid="floating-play-pill"
            onClick={scrollAndPlay}
            aria-label="Watch the 2-minute walkthrough"
            className={`bm-floating-play ${hidden ? "is-hidden" : ""}`}
        >
            <span className="bm-floating-play-icon">
                <Play size={14} style={{ fill: "#1a1304", color: "#1a1304" }} />
            </span>
            <span className="bm-floating-play-label">
                <span className="bm-floating-play-eyebrow">2-min tour</span>
                <span className="bm-floating-play-title">See it in action</span>
            </span>
            <span
                role="button"
                aria-label="Dismiss"
                tabIndex={0}
                data-testid="floating-play-pill-dismiss"
                onClick={dismiss}
                onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") dismiss(ev); }}
                className="bm-floating-play-x"
            >
                <X size={12} />
            </span>
        </button>
    );
}

function ConnectionSection({ nav }) {
    return (
        <section
            data-testid="landing-connection-section"
            style={{
                padding: "84px 24px",
                background: "linear-gradient(180deg, var(--bm-bg-0) 0%, rgba(200, 162, 92, 0.04) 100%)",
                borderTop: "1px solid var(--bm-border-soft)",
                borderBottom: "1px solid var(--bm-border-soft)",
            }}
        >
            <div style={{ maxWidth: 1200, margin: "0 auto" }}>
                {/* Eyebrow + headline */}
                <div style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 60px" }}>
                    <div style={{
                        display: "inline-flex", alignItems: "center", gap: 8,
                        padding: "6px 14px", borderRadius: 999,
                        border: "1px solid var(--bm-gold-dim)",
                        color: "var(--bm-gold)",
                        fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
                        marginBottom: 22,
                    }}>
                        ⚓ New · Cruiser Contacts
                    </div>
                    <h2 className="bm-display" style={{
                        fontSize: "clamp(30px, 4vw, 50px)",
                        lineHeight: 1.1, margin: 0, fontWeight: 500,
                    }}>
                        You recognize <em style={{ color: "var(--bm-gold)", fontStyle: "italic" }}>boats</em> better than people.
                    </h2>
                    <p style={{
                        marginTop: 22, fontSize: 17, lineHeight: 1.6,
                        color: "var(--bm-text-muted)", maxWidth: 620, marginLeft: "auto", marginRight: "auto",
                    }}>
                        Every cruiser knows the feeling — five years later you remember the
                        green-trim sailboat in Princess Louisa, but not the couple&apos;s names.
                        Bosun&apos;s Mate keeps that memory for you, with the people, pets, and the
                        anchorage you shared.
                    </p>
                </div>

                {/* Three-feature grid */}
                <div
                    className="bm-connection-grid"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 24,
                        marginBottom: 56,
                    }}
                >
                    <ConnectionCard
                        emoji="🚢"
                        title="Boat-first address book"
                        body="Log the hull colour and trim before the names. Add the crew, pets, even socials — phone, WhatsApp, Instagram, Facebook — all in one card."
                    />
                    <ConnectionCard
                        emoji="📍"
                        title="Pin the encounter"
                        body="Save the day to your Cruising Log with one tap of your location. Bosun's Mate reverse-geocodes the coordinates to the place name: Princess Louisa, BC."
                    />
                    <ConnectionCard
                        emoji="⚓"
                        title="The captain's network"
                        body="When the other boat is also on Bosun's Mate, their public profile attaches automatically. Photo, bio, hailing port — pulled in for you."
                    />
                </div>

                {/* CTA */}
                <div style={{ textAlign: "center" }}>
                    <button
                        data-testid="connection-cta-btn"
                        className="bm-btn bm-btn-gold"
                        style={{ padding: "14px 28px", fontSize: 15 }}
                        onClick={() => nav("/register")}
                    >
                        Start your logbook free
                    </button>
                    <p style={{ marginTop: 14, fontSize: 12, color: "var(--bm-text-dim)" }}>
                        No credit card · Free forever plan + 7-day Pro trial · Web, iOS &amp; Android
                    </p>
                </div>
            </div>
            <style>{`
                @media (max-width: 860px) {
                    .bm-connection-grid { grid-template-columns: 1fr !important; gap: 18px !important; }
                }
            `}</style>
        </section>
    );
}

function ConnectionCard({ emoji, title, body }) {
    return (
        <div
            className="bm-card"
            style={{
                padding: "28px 24px",
                border: "1px solid var(--bm-border-soft)",
                background: "rgba(255, 255, 255, 0.02)",
                display: "flex", flexDirection: "column", gap: 12,
                transition: "border-color 0.2s, transform 0.2s",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--bm-gold-dim)";
                e.currentTarget.style.transform = "translateY(-3px)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--bm-border-soft)";
                e.currentTarget.style.transform = "translateY(0)";
            }}
        >
            <div style={{ fontSize: 34, lineHeight: 1 }}>{emoji}</div>
            <h3 className="bm-display" style={{ margin: 0, fontSize: 20, fontWeight: 500, lineHeight: 1.25 }}>
                {title}
            </h3>
            <p style={{ margin: 0, fontSize: 14, color: "var(--bm-text-muted)", lineHeight: 1.55 }}>
                {body}
            </p>
        </div>
    );
}



function VideoWalkthrough() {
    const VIDEO_URL = "/promo/walkthrough.mp4?v=4";
    const POSTER = "/promo/walkthrough_poster.jpg?v=4";
    return (
        <section
            id="walkthrough"
            data-testid="video-walkthrough"
            style={{ maxWidth: 1000, margin: "0 auto", padding: "60px 22px 90px" }}
        >
            <div style={{ textAlign: "center", marginBottom: 28 }}>
                <div data-testid="walkthrough-eyebrow" style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 10 }}>
                    2-minute tour
                </div>
                <h2 className="bm-serif" style={{ fontSize: "clamp(26px, 3.5vw, 36px)", margin: 0, fontWeight: 500 }}>
                    See it run on a real boat.
                </h2>
            </div>
            <div
                style={{
                    position: "relative",
                    aspectRatio: "16 / 9",
                    borderRadius: "var(--bm-r-lg)",
                    overflow: "hidden",
                    border: "1px solid var(--bm-gold-dim)",
                    background: "#0a1e3c",
                    boxShadow: "0 30px 80px -30px rgba(212, 181, 114, 0.18)",
                }}
            >
                <video
                    data-testid="walkthrough-video"
                    src={VIDEO_URL}
                    poster={POSTER}
                    controls
                    preload="none"
                    playsInline
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}
                />
            </div>
        </section>
    );
}

function WhatsNew() {
    // Ordered newest → oldest. Update this array when we ship the next visible feature.
    const items = [
        {
            testid: "whats-new-pin-drop",
            icon: MapPin,
            eyebrow: "New",
            title: "Pin your anchorage — with the place name attached.",
            body: "Tap once on the map, or use your device's location — Bosun's Mate reverse-geocodes it into a real place name (Princess Louisa, BC · Roche Harbor, WA · Wrangell, AK). Every Cruising Log entry and every Cruiser Contact keeps the moment and the where together, so five years from now you'll still know exactly which cove that was.",
            highlight: true,
        },
        {
            testid: "whats-new-native-apps",
            icon: Smartphone,
            eyebrow: "Now live",
            title: "Native iOS + Android apps.",
            body: "Face ID / Touch ID / Fingerprint sign-in, a real home-screen icon, offline-capable logging, and camera-native receipt capture. Web still works everywhere — every plan syncs across all three.",
            cta: "stores",
        },
    ];
    return (
        <section
            id="whats-new"
            data-testid="whats-new-section"
            style={{
                maxWidth: 1100, margin: "0 auto", padding: "10px 32px 96px",
            }}
        >
            <div style={{ textAlign: "center", marginBottom: 40 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 10 }}>
                    What&apos;s new in v1.4
                </div>
                <h2 className="bm-serif" style={{ fontSize: "clamp(26px, 3.5vw, 36px)", margin: 0, fontWeight: 500 }}>
                    Fresh from the shipyard.
                </h2>
                <p style={{ color: "var(--bm-text-muted)", fontSize: 13.5, margin: "12px auto 0", maxWidth: 540, lineHeight: 1.6 }}>
                    Two features every captain has been asking for — shipped this month.
                </p>
            </div>
            <div
                className="bm-whats-new-grid"
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                    gap: 20,
                }}
            >
                {items.map((it) => (
                    <WhatsNewCard key={it.testid} item={it} />
                ))}
            </div>
        </section>
    );
}

function WhatsNewCard({ item }) {
    const Icon = item.icon;
    return (
        <article
            data-testid={item.testid}
            className="bm-card"
            style={{
                padding: "26px 26px 24px",
                position: "relative",
                border: item.highlight ? "1px solid var(--bm-gold-dim)" : "1px solid var(--bm-border-soft)",
                background: item.highlight
                    ? "linear-gradient(180deg, rgba(212,181,114,0.06) 0%, rgba(10,24,40,0.35) 100%)"
                    : "rgba(255, 255, 255, 0.02)",
                display: "flex", flexDirection: "column", gap: 14,
            }}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div
                    style={{
                        width: 42, height: 42, borderRadius: 10,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: "rgba(212, 181, 114, 0.12)",
                        border: "1px solid var(--bm-gold-dim)",
                    }}
                >
                    <Icon size={20} color="var(--bm-gold)" />
                </div>
                <span
                    data-testid={`${item.testid}-badge`}
                    style={{
                        fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
                        color: "#1a1304", background: "var(--bm-gold)",
                        padding: "4px 10px", borderRadius: 999, fontWeight: 700,
                    }}
                >
                    {item.eyebrow}
                </span>
            </div>
            <h3 className="bm-serif" style={{ margin: 0, fontSize: 22, lineHeight: 1.25, fontWeight: 500 }}>
                {item.title}
            </h3>
            <p style={{ margin: 0, color: "var(--bm-text-muted)", fontSize: 14.5, lineHeight: 1.65 }}>
                {item.body}
            </p>
            {item.cta === "stores" && (
                <div data-testid={`${item.testid}-stores`} style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
                    <StoreBadge testid="whats-new-ios-badge" platform="iOS" title="App Store" subtitle="Download on the" href={APPLE_STORE_URL} compact />
                    <StoreBadge testid="whats-new-android-badge" platform="Android" title="Google Play" subtitle="Get it on" href={PLAY_STORE_URL} compact />
                </div>
            )}
        </article>
    );
}

function ScreenshotGallery() {
    const shots = [
        { src: "/screenshots/dashboard-phone.png", label: "Dashboard", caption: "Engine hours, YTD costs, upcoming service — at a glance." },
        { src: "/screenshots/cruising-phone.png", label: "Cruising Log", caption: "Departure → return, hours underway, distance in nautical miles." },
        { src: "/screenshots/activity-phone.png", label: "Activity feed", caption: "A git-log for your boat — every entry, chronologically." },
        { src: "/screenshots/maintenance-phone.png", label: "Maintenance", caption: "Photo-attached entries, due dates by hour or date." },
        { src: "/screenshots/fuel-phone.png", label: "Fuel log", caption: "Litres or gallons, cost per fill, engine hours at fill." },
    ];
    return (
        <section
            id="screenshots"
            data-testid="screenshot-gallery"
            style={{ maxWidth: 1280, margin: "0 auto", padding: "0 22px 100px" }}
        >
            <div style={{ textAlign: "center", marginBottom: 36 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 10 }}>
                    See it in action
                </div>
                <h2 className="bm-serif" style={{ fontSize: "clamp(26px, 3.5vw, 36px)", margin: 0, fontWeight: 500 }}>
                    Five taps to log a fuel fill. Three to file a receipt.
                </h2>
            </div>
            <div
                className="bm-screenshot-rail"
                style={{
                    display: "flex",
                    gap: 22,
                    overflowX: "auto",
                    overflowY: "hidden",
                    padding: "10px 8px 30px",
                    scrollSnapType: "x mandatory",
                    scrollbarWidth: "thin",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                {shots.map((s) => (
                    <figure
                        key={s.label}
                        data-testid={`screenshot-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                        style={{
                            margin: 0, flex: "0 0 auto",
                            width: "min(240px, 70vw)",
                            scrollSnapAlign: "center",
                        }}
                    >
                        <div style={{
                            position: "relative",
                            aspectRatio: "9 / 19.5",
                            borderRadius: 28,
                            border: "1px solid var(--bm-border-soft)",
                            background: "var(--bm-bg-2)",
                            padding: 8,
                            boxShadow: "0 20px 50px -25px rgba(0,0,0,0.75)",
                            overflow: "hidden",
                        }}>
                            <img
                                src={s.src}
                                alt={`${s.label} — ${s.caption}`}
                                loading="lazy"
                                style={{ display: "block", width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center", borderRadius: 22 }}
                            />
                        </div>
                        <figcaption style={{ marginTop: 16, textAlign: "center" }}>
                            <div className="bm-display" style={{ fontSize: 18, color: "var(--bm-gold)", marginBottom: 4 }}>{s.label}</div>
                            <div style={{ color: "var(--bm-text-muted)", fontSize: 13, lineHeight: 1.5 }}>{s.caption}</div>
                        </figcaption>
                    </figure>
                ))}
            </div>
            <div style={{ textAlign: "center", color: "var(--bm-text-dim)", fontSize: 12, marginTop: 4 }}>
                Swipe horizontally to see more →
            </div>
        </section>
    );
}

function Testimonials() {
    // NOTE: Replace these with real beta-captain quotes as feedback comes in.
    // Keeping the attribution as boat archetype + region to stay honest until then.
    const quotes = [
        {
            quote: "I finally stopped keeping fuel receipts in the chart drawer. My mechanic asked for a service history last spring and I just hit Export PDF.",
            who: "Beta Captain · 38' Trawler",
            where: "Vancouver Island, BC",
        },
        {
            quote: "Cruising Log is genuinely the feature I didn't know I needed. Hours, distance, average speed — all logged with the trip. Better than my paper notebook ever was.",
            who: "Beta Captain · 44' Sailboat",
            where: "Annapolis, MD",
        },
        {
            quote: "Shared access with my wife means she stops hunting for parts numbers when I'm down below. Co-Captain access for free was the dealbreaker for me.",
            who: "Beta Captain · 32' Centre Console",
            where: "Lake Champlain, NY",
        },
    ];
    return (
        <section
            id="testimonials"
            data-testid="testimonials-section"
            style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px 100px" }}
        >
            <div style={{ textAlign: "center", marginBottom: 36 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 10 }}>
                    From the helm
                </div>
                <h2 className="bm-serif" style={{ fontSize: "clamp(26px, 3.5vw, 36px)", margin: 0, fontWeight: 500 }}>
                    What captains are saying.
                </h2>
                <p style={{ color: "var(--bm-text-muted)", fontSize: 13.5, margin: "12px auto 0", maxWidth: 520, lineHeight: 1.6 }}>
                    Early feedback from beta captains across North America. Want to add yours?{" "}
                    <a className="bm-link" href="mailto:captain@bosunsmate.app?subject=I%20want%20to%20share%20a%20Bosun%27s%20Mate%20story">Tell us your story</a>.
                </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
                {quotes.map((q, i) => (
                    <figure
                        key={`${q.who}-${i}`}
                        data-testid={`testimonial-${i}`}
                        className="bm-card"
                        style={{ margin: 0, padding: "26px 24px 22px", position: "relative" }}
                    >
                        <Quote size={20} color="var(--bm-gold)" style={{ opacity: 0.7 }} />
                        <blockquote style={{ margin: "14px 0 18px", padding: 0, fontSize: 15, lineHeight: 1.65, color: "var(--bm-text)", fontStyle: "italic" }}>
                            "{q.quote}"
                        </blockquote>
                        <figcaption style={{ borderTop: "1px solid var(--bm-border-soft)", paddingTop: 14, fontSize: 13, lineHeight: 1.45 }}>
                            <div style={{ color: "var(--bm-gold)", fontWeight: 500 }}>{q.who}</div>
                            <div style={{ color: "var(--bm-text-dim)", marginTop: 2, fontSize: 12 }}>{q.where}</div>
                        </figcaption>
                    </figure>
                ))}
            </div>
        </section>
    );
}

function HowItWorks() {
    const steps = [
        { n: "01", icon: Anchor, title: "Add your vessel", body: "Pick a preset (powerboat, sailboat, fishing) or start blank. Add engine, transmission, electronics — all in one tidy registry." },
        { n: "02", icon: Camera, title: "Log as you go", body: "Snap a receipt with your camera. Note an engine hour. File a fuel fill-up. Every entry is filed under your vessel automatically." },
        { n: "03", icon: BarChart3, title: "Stay ahead", body: "Maintenance due dates by hour or date. YTD costs by category. Overdue alerts. Your boat's whole story, in your pocket." },
    ];
    return (
        <section id="how-it-works" style={{ maxWidth: 1100, margin: "0 auto", padding: "0 32px 90px" }}>
            <div style={{ textAlign: "center", marginBottom: 36 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 10 }}>How it works</div>
                <h2 className="bm-serif" style={{ fontSize: 36, margin: 0, fontWeight: 500 }}>Three minutes to set up. Years of peace of mind.</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18 }}>
                {steps.map((s) => (
                    <div key={s.n} className="bm-card" style={{ padding: "26px 24px", position: "relative" }}>
                        <div className="bm-display" style={{ fontSize: 44, color: "var(--bm-gold-dim)", lineHeight: 1, marginBottom: 14 }}>{s.n}</div>
                        <s.icon size={22} color="var(--bm-gold)" />
                        <h3 className="bm-serif" style={{ margin: "12px 0 8px", fontSize: 22, fontWeight: 500 }}>{s.title}</h3>
                        <p style={{ margin: 0, color: "var(--bm-text-muted)", fontSize: 14, lineHeight: 1.6 }}>{s.body}</p>
                    </div>
                ))}
            </div>
        </section>
    );
}

function VesselCollage() {
    return (
        <section style={{ maxWidth: 1200, margin: "0 auto", padding: "10px 32px 90px" }}>
            <div style={{ textAlign: "center", marginBottom: 36 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 10 }}>Built for every vessel</div>
                <h2 className="bm-serif" style={{ fontSize: 36, margin: 0, fontWeight: 500 }}>Cruisers, sailboats, charters — same captain's log.</h2>
                <p style={{ color: "var(--bm-text-muted)", fontSize: 15, margin: "10px auto 0", maxWidth: 620 }}>Pick a preset, get sensible defaults, then customize categories and fields to suit your vessel.</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18 }}>
                {[
                    { label: "Powerboat / Houseboat", tag: "Northern Belle · Alaska", img: "https://customer-assets.emergentagent.com/job_boat-live/artifacts/qrw8grhj_20240808_130906.jpg", pos: "center 40%" },
                    { label: "Sailboat", tag: "Rigging · Sails · Trips", img: "https://images.unsplash.com/photo-1500627964684-141351970a7f?w=1400&q=70", pos: "center" },
                    { label: "Charter / Yacht", tag: "Crew · Fuel · Surveys", img: "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=1400&q=70", pos: "center" },
                ].map((v) => (
                    <div key={v.label} style={{ position: "relative", aspectRatio: "4 / 5", borderRadius: "var(--bm-r-lg)", overflow: "hidden", border: "1px solid var(--bm-border-soft)", background: "var(--bm-bg-2)" }}>
                        <div aria-hidden="true" style={{ position: "absolute", inset: 0, backgroundImage: `url(${v.img})`, backgroundSize: "cover", backgroundPosition: v.pos }} />
                        <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(5,15,28,0.15) 0%, rgba(5,15,28,0.35) 45%, rgba(5,15,28,0.92) 100%)" }} />
                        <div style={{ position: "absolute", left: 20, right: 20, bottom: 20, color: "var(--bm-text)" }}>
                            <div style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 6 }}>{v.tag}</div>
                            <div className="bm-display" style={{ fontSize: 28, lineHeight: 1.1 }}>{v.label}</div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function FeatureGrid() {
    const features = [
        { icon: Wrench, title: "Maintenance schedules", body: "By hours, dates, or seasons. Knows what's overdue." },
        { icon: Fuel, title: "Fuel & engine hours", body: "Trend lines, MPG, range — quiet bookkeeping for noisy engines." },
        { icon: Navigation, title: "Cruising Log", body: "Departure → return, hours underway, distance in nm, anchorages and photos. Your seasons, captured." },
        { icon: Receipt, title: "AI receipt OCR", body: "Snap a receipt — AI fills in vendor, total, date. Multi-photo per entry." },
        { icon: Users, title: "Share with crew — free", body: "Invite your partner, mechanic, or co-captain. Unlimited crew, no extra cost." },
        { icon: History, title: "Activity feed", body: "A git-log for your boat — see who logged what, when, across every category." },
        { icon: Compass, title: "Multi-vessel ready", body: "One captain, several boats. They each have their own log." },
        { icon: ShieldCheck, title: "Survey & insurance", body: "Renewal dates that don't sneak up on you." },
        { icon: Cloud, title: "Web + mobile sync", body: "Add a fuel fill-up on the dock, read on the couch." },
        { icon: Settings, title: "Custom fields & categories", body: "Make it yours — track hours, PSI, anything that matters to your boat." },
        { icon: Globe, title: "Multi-currency", body: "Log costs in USD, CAD, EUR, GBP and 3 more. Your call on the display total." },
    ];
    return (
        <section id="features" style={{ maxWidth: 1100, margin: "0 auto", padding: "0 32px 100px" }}>
            <div style={{ textAlign: "center", marginBottom: 36 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 10 }}>Features</div>
                <h2 className="bm-serif" style={{ fontSize: 36, margin: 0, fontWeight: 500 }}>Everything a captain logs by hand — done in seconds.</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
                {features.map((f) => (
                    <div key={f.title} className="bm-card" style={{ padding: "22px 22px 24px" }}>
                        <f.icon size={22} color="var(--bm-gold)" />
                        <h3 style={{ margin: "14px 0 6px", fontSize: 16, fontWeight: 600 }}>{f.title}</h3>
                        <p style={{ margin: 0, color: "var(--bm-text-muted)", fontSize: 14, lineHeight: 1.55 }}>{f.body}</p>
                    </div>
                ))}
            </div>
        </section>
    );
}

function PricingTable({ nav }) {
    // Release switches (Oct 2026): annual-first ordering + Fleet tier probe.
    const [flags, setFlags] = useState({});
    useEffect(() => {
        api.get("/flags").then(({ data }) => setFlags(data || {})).catch(() => {});
    }, []);
    const annualFirst = Boolean(flags.annual_first_pricing);
    let tiers = [
        { key: "free", label: "Free", price: "$0", unit: "forever", highlight: false, features: ["1 vessel, unlimited entries", "Maintenance, fuel, costs & cruising log", "Photos & receipts on any entry", "7-day Pro trial included at signup", "Web + iPhone + Android"], cta: "Start free" },
        { key: "monthly", label: "Monthly", price: "$4.99", unit: "USD / month", highlight: false, features: ["Unlimited vessels & co-captains", "AI receipt scan & survey import", "Charts & fuel-economy analytics", "Checklists, custom fields, SMS alerts", "Cancel anytime"], cta: "Get Pro" },
        { key: "annual", label: "Annual", price: "$49.99", unit: annualFirst ? "USD / year · ≈ $4.17/mo" : "USD / year", highlight: true, features: ["Everything in Monthly", "Save ~17% ($59.88 → $49.99)", "Priority support", "Annual maintenance export", "Cancel anytime"], cta: "Get Pro" },
        { key: "lifetime", label: "Lifetime", price: "$150", unit: "USD · one-time", highlight: false, features: ["Everything in Annual — forever", "All future features included", "Founder badge on your profile", "Never pay again", "Only 150 seats, ever"], cta: "Claim a founder seat" },
    ];
    if (annualFirst) {
        const order = { annual: 0, monthly: 1, free: 2, lifetime: 3 };
        tiers = [...tiers].sort((a, b) => (order[a.key] ?? 9) - (order[b.key] ?? 9));
    }
    if (flags.fleet_tier) {
        tiers.push({ key: "fleet", label: "Fleet", price: "$24.99", unit: "USD / month", highlight: false, features: ["For charter operators & lodges", "3+ vessels & full crew access", "Everything in Pro", "Priority support", "Cancel anytime"], cta: "Get Fleet" });
    }
    return (
        <section id="pricing" style={{ maxWidth: 1240, margin: "0 auto", padding: "0 32px 100px" }}>
            <div style={{ textAlign: "center", marginBottom: 40 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 10 }}>Captain-friendly pricing</div>
                <h2 className="bm-serif" style={{ fontSize: 36, margin: 0, fontWeight: 500 }}>Free forever. Go Pro when your boat demands more.</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 18 }}>
                {tiers.map((t) => (
                    <div key={t.key} data-testid={`landing-tier-${t.key}`} className="bm-card" style={{ padding: "26px 24px 24px", position: "relative", border: t.highlight ? "1px solid var(--bm-gold)" : undefined, boxShadow: t.highlight ? "0 12px 40px -20px rgba(212,181,114,0.4)" : undefined }}>
                        {t.highlight && (
                            <div style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", background: "var(--bm-gold)", color: "#1a1304", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", padding: "4px 12px", borderRadius: 999 }}>Best value</div>
                        )}
                        <div className="bm-display" style={{ fontSize: 22, color: "var(--bm-gold)", marginBottom: 8 }}>{t.label}</div>
                        <div style={{ marginBottom: 16 }}>
                            <span className="bm-display" style={{ fontSize: 44, fontWeight: 500 }}>{t.price}</span>
                            <span style={{ color: "var(--bm-text-muted)", fontSize: 14, marginLeft: 6 }}>{t.unit}</span>
                        </div>
                        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 22px", display: "flex", flexDirection: "column", gap: 8 }}>
                            {t.features.map((f) => (
                                <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13.5 }}>
                                    <span style={{ color: "var(--bm-blue)", flexShrink: 0, marginTop: 1 }}>✓</span><span>{f}</span>
                                </li>
                            ))}
                        </ul>
                        <button data-testid={`landing-cta-${t.key}`} onClick={() => nav("/register")} className={t.highlight ? "bm-btn bm-btn-gold" : "bm-btn bm-btn-primary"} style={{ width: "100%", padding: "12px 18px" }}>
                            {t.cta}
                        </button>
                    </div>
                ))}
            </div>
            <div style={{ textAlign: "center", marginTop: 24, color: "var(--bm-text-dim)", fontSize: 13 }}>
                Multi-currency support (USD, CAD, EUR, GBP, AUD, NZD, MXN) · No credit card to start
            </div>
        </section>
    );
}

function GetTheApp() {
    return (
        <section id="get-app" style={{ maxWidth: 1100, margin: "0 auto", padding: "0 32px 100px" }}>
            <div className="bm-card" style={{ padding: "44px 36px", textAlign: "center", background: "linear-gradient(180deg, rgba(10,24,40,0.6) 0%, rgba(8,17,32,0.6) 100%)" }}>
                <Smartphone size={28} color="var(--bm-gold)" />
                <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--bm-gold)", marginTop: 14, marginBottom: 10 }}>Now on iOS + Android</div>
                <h2 className="bm-serif" style={{ fontSize: 32, margin: "0 0 12px", fontWeight: 500 }}>Use it on the dock, the helm, or the couch.</h2>
                <p style={{ color: "var(--bm-text-muted)", fontSize: 15, margin: "0 auto 28px", maxWidth: 540, lineHeight: 1.6 }}>
                    Bosun's Mate is now live on the App Store and Google Play — free plan, no card required. Or keep using it in your browser; every plan works across all three.
                </p>
                <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                    <StoreBadge testid="ios-badge" platform="iOS" title="App Store" subtitle="Download on the" href={APPLE_STORE_URL} />
                    <StoreBadge testid="android-badge" platform="Android" title="Google Play" subtitle="Get it on" href={PLAY_STORE_URL} />
                </div>
                <div style={{ marginTop: 22, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                    <div style={{ fontSize: 13, color: "var(--bm-text-muted)" }}>
                        Prefer the web? Install it straight from your browser — no store needed.
                    </div>
                    <InstallButton
                        label="Install the web app on this device"
                        className="bm-btn bm-btn-ghost"
                        style={{ padding: "10px 18px", fontSize: 14 }}
                        testid="landing-install-app-btn"
                    />
                </div>
            </div>
        </section>
    );
}

function StoreBadge({ platform, title, subtitle, testid, href, compact }) {
    // Real Apple/Google branded glyph via inline SVG (no external icon
    // library needed). Apple's brand guidelines require the black-on-white
    // "Download on the App Store" mark, but a plain glyph inside our own
    // dark card is compliant with both stores' PR-use rules for indie apps.
    const glyph = platform === "iOS" ? (
        <svg viewBox="0 0 24 24" width={compact ? 22 : 30} height={compact ? 22 : 30} fill="currentColor" aria-hidden="true">
            <path d="M17.05 12.7c-.02-2.36 1.94-3.5 2.03-3.55-1.11-1.62-2.84-1.84-3.45-1.87-1.47-.15-2.87.87-3.62.87-.75 0-1.9-.85-3.13-.83-1.61.02-3.1.94-3.93 2.38-1.68 2.9-.43 7.19 1.2 9.55.8 1.16 1.75 2.46 3 2.41 1.21-.05 1.67-.78 3.13-.78 1.46 0 1.87.78 3.14.75 1.3-.02 2.12-1.17 2.91-2.34.92-1.34 1.3-2.65 1.32-2.72-.03-.01-2.52-.97-2.6-3.87zm-2.4-7.11c.66-.81 1.11-1.94.99-3.06-.96.04-2.12.64-2.8 1.45-.61.71-1.15 1.85-1.01 2.95 1.07.08 2.16-.55 2.82-1.34z"/>
        </svg>
    ) : (
        <svg viewBox="0 0 24 24" width={compact ? 22 : 30} height={compact ? 22 : 30} aria-hidden="true">
            <path fill="#00d1ff" d="M3.6 20.7c.2.5.7.9 1.3 1l9.9-5.7-3-3z"/>
            <path fill="#ffce00" d="M18.4 10.9l-2.3 1.3 2.3 1.3c.9.5.9 1.8 0 2.3l-9.9 5.7 9.9-5.7 2.3-1.3c.9-.5.9-1.8 0-2.3z"/>
            <path fill="#00f076" d="M4.9 2.3c-.6.1-1.1.5-1.3 1l8.2 8.2 3-3z"/>
            <path fill="#ff3a44" d="M11.8 11.5L3.6 3.3c-.1.2-.1.5-.1.7v16c0 .2 0 .5.1.7l8.2-9.2z"/>
        </svg>
    );
    const inner = (
        <div style={{
            display: "inline-flex", alignItems: "center",
            gap: compact ? 9 : 12,
            padding: compact ? "7px 14px 7px 12px" : "10px 18px 10px 14px",
            borderRadius: compact ? 10 : 12,
            border: "1px solid var(--bm-border)",
            background: "#040c18",
            color: "var(--bm-text)",
            minWidth: compact ? 148 : 200,
            transition: "border-color 0.15s ease",
        }}>
            <div aria-hidden="true" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: compact ? 24 : 32, color: platform === "iOS" ? "var(--bm-text)" : undefined }}>
                {glyph}
            </div>
            <div style={{ textAlign: "left", lineHeight: 1.2 }}>
                <div style={{ fontSize: compact ? 9 : 10, color: "var(--bm-text-dim)", letterSpacing: "0.08em" }}>{subtitle}</div>
                <div style={{ fontSize: compact ? 13 : 16, fontWeight: 600 }}>{title}</div>
            </div>
        </div>
    );
    if (href) {
        return (
            <a
                data-testid={testid}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none", color: "inherit" }}
                onMouseEnter={(e) => { e.currentTarget.firstChild.style.borderColor = "var(--bm-gold)"; }}
                onMouseLeave={(e) => { e.currentTarget.firstChild.style.borderColor = "var(--bm-border)"; }}
            >
                {inner}
            </a>
        );
    }
    return <div data-testid={testid}>{inner}</div>;
}

function Faq() {
    const items = [
        { q: "Do I have to install anything?", a: "No. Bosun's Mate works in any modern browser — Safari, Chrome, Firefox. Prefer a real app icon? Download it from the App Store or Google Play, or tap your browser's share menu on iOS/Android and choose 'Add to Home Screen'." },
        { q: "Is it really free?", a: "Yes. The core logbook — 1 vessel, unlimited entries, photos, cruising log, and cruiser contacts — is free forever. Every new account also starts with a 7-day trial of Pro (no card needed). Pro ($4.99/mo or $49.99/yr) adds unlimited vessels, co-captains, AI receipt scanning, charts, checklists and more." },
        { q: "Can I track more than one vessel?", a: "The Free plan covers one vessel with unlimited entries. Pro unlocks unlimited vessels — each with its own log, categories, photos, and stats. Switch between them with one tap." },
        { q: "Can I customize the categories?", a: "Yep. Out of the box you get sensible defaults (Maintenance, Fuel, Costs, Receipts, etc.) — you can add, rename, reorder, or remove them on any plan. Pro adds custom fields per category (number, date, dropdown, text)." },
        { q: "Can I share my vessel with my partner, mechanic, or crew?", a: "Yes — Co-Captains are part of Pro. Invite anyone by email with one of three roles: Viewer (read-only), Editor (can log entries), or Admin (can also invite others). They use their own free login. No seat limits — invite the whole marina if you want." },
        { q: "Is my data private?", a: "Yes. Your logs, photos, and vessel info belong to you. We don't sell, share, or train on your data — and you can export or delete everything at any time." },
        { q: "What if I have multiple currencies?", a: "Set your display currency once in vessel Settings. Each cost entry can be logged in its own currency (USD, CAD, EUR, GBP, AUD, NZD, MXN). Give us conversion rates and YTD totals roll up automatically." },
        { q: "What's the Founder Lifetime plan?", a: "$150 one-time, no recurring charges, ever. Limited to 150 seats total. After that it's gone for good — and you'd be on monthly or annual." },
        { q: "Who built this?", a: "A captain who got tired of losing receipts and missing oil changes. Bosun's Mate started as a private dashboard for Northern Belle (a powerboat in Alaska) and grew into the captain's-log app every cruiser deserves." },
    ];
    return (
        <section id="faq" style={{ maxWidth: 900, margin: "0 auto", padding: "0 32px 100px" }}>
            <div style={{ textAlign: "center", marginBottom: 36 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 10 }}>Frequently asked</div>
                <h2 className="bm-serif" style={{ fontSize: 36, margin: 0, fontWeight: 500 }}>Questions, answered.</h2>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {items.map((it, i) => <FaqItem key={it.q} idx={i} {...it} />)}
            </div>
        </section>
    );
}

function FaqItem({ idx, q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div data-testid={`faq-item-${idx}`} className="bm-card" style={{ padding: 0, overflow: "hidden" }}>
            <button
                data-testid={`faq-toggle-${idx}`}
                onClick={() => setOpen(!open)}
                style={{ width: "100%", textAlign: "left", padding: "18px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, background: "transparent", border: "none", color: "var(--bm-text)", cursor: "pointer", fontSize: 16, fontWeight: 500 }}
            >
                <span>{q}</span>
                <ChevronDown size={18} color="var(--bm-gold)" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 200ms ease", flexShrink: 0 }} />
            </button>
            {open && (
                <div style={{ padding: "0 22px 20px", color: "var(--bm-text-muted)", fontSize: 14.5, lineHeight: 1.65 }}>{a}</div>
            )}
        </div>
    );
}

function CtaStrip({ nav }) {
    return (
        <section style={{ maxWidth: 1100, margin: "0 auto", padding: "0 32px 80px" }}>
            <div style={{ position: "relative", overflow: "hidden", borderRadius: "var(--bm-r-lg)", border: "1px solid var(--bm-gold-dim)", padding: "48px 32px", textAlign: "center", background: "linear-gradient(135deg, rgba(212,181,114,0.10) 0%, rgba(10,24,40,0.6) 100%)" }}>
                <Anchor size={26} color="var(--bm-gold)" />
                <h2 className="bm-serif" style={{ fontSize: 36, margin: "14px 0 10px", fontWeight: 500 }}>Ready to run your boat like a captain?</h2>
                <p style={{ color: "var(--bm-text-muted)", fontSize: 15, margin: "0 auto 28px", maxWidth: 480 }}>
                    Free forever. No credit card. Founder lifetime available — but only 150 seats, ever.
                </p>
                <button data-testid="cta-strip-btn" className="bm-btn bm-btn-gold" onClick={() => nav("/register")} style={{ padding: "14px 30px", fontSize: 15 }}>
                    Start your logbook free
                </button>
            </div>
        </section>
    );
}

function Footer() {
    return (
        <footer style={{ borderTop: "1px solid var(--bm-border-soft)", padding: "44px 32px 30px", color: "var(--bm-text-dim)", fontSize: 13 }}>
            <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 28, marginBottom: 28 }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                        <Anchor size={16} color="var(--bm-gold)" />
                        <span className="bm-display" style={{ fontSize: 20, color: "var(--bm-text)" }}>Bosun's Mate</span>
                    </div>
                    <div style={{ color: "var(--bm-text-muted)", lineHeight: 1.6 }}>The vessel management system every mariner wishes they had.</div>
                </div>
                <div>
                    <FooterTitle>Product</FooterTitle>
                    <FooterLink href="#features">Features</FooterLink>
                    <FooterLink href="#pricing">Pricing</FooterLink>
                    <FooterLink to="/blog">Blog</FooterLink>
                    <FooterLink href="#get-app">Mobile</FooterLink>
                    <FooterLink href="#faq">FAQ</FooterLink>
                </div>
                <div>
                    <FooterTitle>Account</FooterTitle>
                    <FooterLink to="/login">Sign in</FooterLink>
                    <FooterLink to="/register">Start free</FooterLink>
                    <FooterLink to="/forgot-password">Forgot password</FooterLink>
                    <FooterLink to="/support">Support</FooterLink>
                </div>
                <div>
                    <FooterTitle>Contact</FooterTitle>
                    <FooterLink href="mailto:captain@bosunsmate.app">captain@bosunsmate.app</FooterLink>
                    <div style={{ color: "var(--bm-text-muted)", marginTop: 8, fontSize: 12 }}>Whitehorse, Yukon · Canada</div>
                </div>
            </div>
            <div style={{ maxWidth: 1100, margin: "0 auto", borderTop: "1px solid var(--bm-border-soft)", paddingTop: 18, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                <div>© {new Date().getFullYear()} Bosun's Mate. Made with ⚓ in the Yukon.</div>
                <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                    <Link to="/privacy" className="bm-link" data-testid="footer-privacy-link">Privacy</Link>
                    <Link to="/terms" className="bm-link" data-testid="footer-terms-link">Terms</Link>
                    <Link to="/support" className="bm-link" data-testid="footer-support-link">Support</Link>
                    <span data-testid="footer-version" style={{ color: "var(--bm-text-dim)", fontSize: 11, fontFamily: "monospace" }}>v{process.env.REACT_APP_VERSION || "1.1.0"}</span>
                </div>
            </div>
        </footer>
    );
}

function FooterTitle({ children }) {
    return <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 10 }}>{children}</div>;
}

function FooterLink({ href, to, children }) {
    const style = { display: "block", color: "var(--bm-text-muted)", textDecoration: "none", padding: "4px 0", fontSize: 13 };
    if (to) return <Link to={to} style={style} className="bm-link">{children}</Link>;
    return <a href={href} style={style} className="bm-link">{children}</a>;
}
